import { Loader } from '@zoom/components';
import clsx from 'clsx';
import { isNil } from 'ramda';
import React, { memo, RefObject, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PerfectScrollbar from 'react-perfect-scrollbar';

import { useAuth } from '../../../../app/AuthorizationProvider';
import { RadioButton } from '../../../../components/RadioButton/RadioButton';
import { EstimatedServiceLevelPerQueue } from '../../../../graphql/components';
import { RecordBooleanType, Values } from '../../../../types';
import { MissingForecastPerQueue, QueuesWithColors, SchedulingChartProps, SchedulingChartType } from '../../types';
import { ChartLegend } from './ChartLegend';
import { MultiQueueChartLegend } from './MultiQueueChartLegend';
import { SLAChartLegend } from './SLAChartLegend';
import { SchedulingChart } from './SchedulingChart';
import { ServiceLevelAgreementChart } from './ServiceLevelAgreementChart';
import { XAxisLabels } from './XAxisLabels';
import { YAxisLabels } from './YAxisLabels';

export type SchedulingChartContainerProps = {
  endTime: number;
  estimatedServiceLevelPerQueue?: Record<string, EstimatedServiceLevelPerQueue>;
  schedulingChartType: Values<typeof SchedulingChartType>;
  isLoadingForecastData: boolean;
  isLoadingSlaData: boolean;
  missingForecastIntervalsPerQueue: MissingForecastPerQueue;
  onScrollXHandler: (callback: (e: HTMLElement) => void) => (e: HTMLElement) => void;
  onSchedulingChartXPositionChangeLeft: (e: HTMLElement) => void;
  onSchedulingChartXPositionChangeRight: (e: HTMLElement) => void;
  onToggleSourceOfForecastData: (forecastType: Values<typeof SchedulingChartType>) => void;
  queueIds: string[];
  queues: QueuesWithColors;
  scrollbarRef: RefObject<PerfectScrollbar>;
  setChartContainerRef: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  schedulingChartState: SchedulingChartProps;
  startTime: number;
  timezone: string;
  width: number;
};

export const prepareAllQueuesVisibleObject = (queueIds: SchedulingChartContainerProps['queueIds']) =>
  queueIds.reduce((queuesState, queueId) => {
    // eslint-disable-next-line no-param-reassign
    queuesState[queueId] = true;

    return queuesState;
  }, {} as RecordBooleanType);

export const SchedulingChartContainerComponent: React.FC<SchedulingChartContainerProps> = ({
  endTime,
  estimatedServiceLevelPerQueue,
  schedulingChartType,
  isLoadingForecastData,
  isLoadingSlaData,
  missingForecastIntervalsPerQueue,
  onScrollXHandler,
  onSchedulingChartXPositionChangeLeft,
  onSchedulingChartXPositionChangeRight,
  queues,
  queueIds,
  scrollbarRef,
  setChartContainerRef,
  schedulingChartState: { yAxisTicks, plotBandsData, dataPerQueue },
  startTime,
  timezone,
  onToggleSourceOfForecastData,
  width,
}) => {
  const { locale } = useAuth();
  const isLoadingData = isLoadingForecastData || isLoadingSlaData;
  const isMultiQueueSchedule = queueIds.length > 1;

  const [dataPerQueueVisibility, setDataPerQueueVisibility] = useState(() => prepareAllQueuesVisibleObject(queueIds));
  const [currentHoveredQueueId, setCurrentHoveredQueueId] = useState<string | undefined>(
    isMultiQueueSchedule ? undefined : queueIds[0]
  );

  // queues with missing SLA should not be a part of SLA chart (only present within legend with disabled flag and tooltip message)
  const queueIdsWithPresentSla = useMemo(
    () =>
      Object.keys(estimatedServiceLevelPerQueue ?? {}).filter(
        (queueId) => estimatedServiceLevelPerQueue?.[queueId].series.benchmark.length
      ),
    [estimatedServiceLevelPerQueue]
  );

  useEffect(() => {
    if (!isMultiQueueSchedule) {
      return;
    }

    const visibleQueuesWithPresentSla = queueIdsWithPresentSla.filter((queueId) => dataPerQueueVisibility[queueId]);

    // redefine currentHoveredQueueId based on currently viewed queues preset and retrieved estimatedServiceLevelPerQueue data
    switch (visibleQueuesWithPresentSla.length) {
      case 1: {
        // when only one visible queue with provided SLA data is present - mark it as hovered, so that the chart shows its benchmark value
        setCurrentHoveredQueueId(visibleQueuesWithPresentSla[0]);
        return;
      }
      default:
        // when 0 or multiple visible queue with provided SLA data are present - reset the hover state, no benchmark value should be visible at this point (until user changes visibility or provides hover action over series data within chart)
        setCurrentHoveredQueueId(undefined);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueIdsWithPresentSla]);

  useEffect(() => {
    if (!isMultiQueueSchedule) {
      return;
    }

    const visibleQueueIds = Object.keys(dataPerQueueVisibility).filter(
      (queueId) => dataPerQueueVisibility[queueId] && estimatedServiceLevelPerQueue?.[queueId].series.benchmark.length
    );

    if (visibleQueueIds.length > 1) {
      // the hovered state should be reset on legend items visibility change
      setCurrentHoveredQueueId(undefined);
      return;
    }

    setCurrentHoveredQueueId(visibleQueueIds[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataPerQueueVisibility]);

  const { t } = useTranslation();

  const toggleQueueVisibility = (queueId: string) => {
    setDataPerQueueVisibility((previousState) => ({
      ...previousState,
      [queueId]: !dataPerQueueVisibility[queueId],
    }));
  };

  const updateVisibilityForAllQueues = () => {
    setDataPerQueueVisibility(prepareAllQueuesVisibleObject(queueIds));
  };

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    onToggleSourceOfForecastData(e.target.value as Values<typeof SchedulingChartType>);
  };

  const isSLAChartEnabled = schedulingChartType === SchedulingChartType.SLA;
  const averageEslOfHoveredQueue =
    currentHoveredQueueId && estimatedServiceLevelPerQueue?.[currentHoveredQueueId].averageEsl;

  return (
    <div className="scheduling-chart__outer-container">
      {isLoadingData && (
        <div className="scheduling-chart__loading-overlay">
          {/* Do not render this loader when loading forecast data as the full-screen loader with overlay is being rendered anyway */}
          {!isLoadingForecastData && isSLAChartEnabled && <Loader color="#63ad1a" size={40} />}
        </div>
      )}
      <div className="scheduling-chart__toolbar">
        <div className="scheduling-chart__forecast-switcher">
          <RadioButton
            checked={schedulingChartType === SchedulingChartType.Production}
            testId="scheduling-chart:legend:button-forecast-production"
            name="forecastType"
            onChange={handleToggle}
            value={SchedulingChartType.Production}
          >
            {t('forecast.type.production')}
          </RadioButton>
          <RadioButton
            checked={schedulingChartType === SchedulingChartType.Intraday}
            name="forecastType"
            onChange={handleToggle}
            testId="scheduling-chart:legend:button-forecast-intraday"
            value={SchedulingChartType.Intraday}
          >
            {t('forecast.type.intraday')}
          </RadioButton>
          <RadioButton
            checked={isSLAChartEnabled}
            name="forecastType"
            onChange={handleToggle}
            testId="scheduling-chart:legend:button-sla"
            value={SchedulingChartType.SLA}
          >
            {t('forecast.type.sla')}
          </RadioButton>
        </div>
        {isSLAChartEnabled && (
          <SLAChartLegend
            dataPerQueueVisibility={dataPerQueueVisibility}
            estimatedServiceLevelPerQueue={estimatedServiceLevelPerQueue}
            isMultiQueueSchedule={isMultiQueueSchedule}
            queues={queues}
            queueIds={queueIds}
            t={t}
            toggleQueueVisibility={toggleQueueVisibility}
            updateVisibilityForAllQueues={updateVisibilityForAllQueues}
          />
        )}
        {!isSLAChartEnabled &&
          (isMultiQueueSchedule ? (
            <MultiQueueChartLegend
              dataPerQueueVisibility={dataPerQueueVisibility}
              queues={queues}
              queueIds={queueIds}
              t={t}
              toggleQueueVisibility={toggleQueueVisibility}
              updateVisibilityForAllQueues={updateVisibilityForAllQueues}
            />
          ) : (
            <ChartLegend t={t} />
          ))}
      </div>
      <div className={clsx('scheduling-chart__wrapper', { 'scheduling-chart__wrapper--sla': isSLAChartEnabled })}>
        {isSLAChartEnabled ? (
          <>
            {currentHoveredQueueId && (
              <div className="scheduling-chart__average-service-level">
                <span
                  className="scheduling-chart__average-service-level__queue-name"
                  style={{ color: queues[currentHoveredQueueId].color }}
                >
                  {queues[currentHoveredQueueId].name}
                </span>
                <span className="scheduling-chart__average-service-level__label">
                  {t('schedule.averageServiceLevel')}
                </span>
                <span
                  className="scheduling-chart__average-service-level__value"
                  style={{ color: queues[currentHoveredQueueId].color }}
                >
                  {isNil(averageEslOfHoveredQueue)
                    ? t('layout.notAvailable')
                    : averageEslOfHoveredQueue.toLocaleString(locale, {
                        maximumFractionDigits: 2,
                        style: 'percent',
                      })}
                </span>
              </div>
            )}
            <YAxisLabels isPercentage ticks={[0, 0.25, 0.5, 0.75, 1]} />
          </>
        ) : (
          <YAxisLabels afterLabels={[t('schedule.overStaffed'), t('schedule.underStaffed')]} ticks={yAxisTicks} />
        )}
        <PerfectScrollbar
          className="scheduling-chart__scrollbar-container"
          containerRef={setChartContainerRef}
          onScrollLeft={onScrollXHandler(onSchedulingChartXPositionChangeLeft)}
          onScrollRight={onScrollXHandler(onSchedulingChartXPositionChangeRight)}
          options={{ suppressScrollY: true }}
          ref={scrollbarRef}
        >
          {isSLAChartEnabled ? (
            <>
              <ServiceLevelAgreementChart
                end={endTime}
                start={startTime}
                currentHoveredQueueId={currentHoveredQueueId}
                dataPerQueueVisibility={dataPerQueueVisibility}
                estimatedServiceLevelPerQueue={estimatedServiceLevelPerQueue}
                isLoadingData={isLoadingData}
                isMultiQueueSchedule={isMultiQueueSchedule && queueIdsWithPresentSla.length > 1}
                missingForecastIntervalsPerQueue={missingForecastIntervalsPerQueue}
                queueIds={queueIdsWithPresentSla}
                queues={queues}
                setCurrentHoveredQueueId={setCurrentHoveredQueueId}
                t={t}
                timezone={timezone}
                width={width}
                yAxisTicks={[0, 0.25, 0.5, 0.75, 1]}
              />
              {/* this div needs to be rendered even as empty element as it keeps the width of container during loading of data, when charts is being resized - it prevents scrolling to the most left position when switching charts types */}
              <div className="scheduling-chart__x-axis-container" />
            </>
          ) : (
            <>
              <SchedulingChart
                dataPerQueue={dataPerQueue}
                dataPerQueueVisibility={dataPerQueueVisibility}
                plotBandsData={plotBandsData}
                isLoadingData={isLoadingData}
                isMultiQueueSchedule={isMultiQueueSchedule}
                missingForecastIntervalsPerQueue={missingForecastIntervalsPerQueue}
                queueIds={queueIds}
                queues={queues}
                t={t}
                timezone={timezone}
                width={width}
                yAxisTicks={yAxisTicks}
              />
              <XAxisLabels
                dataPerQueue={dataPerQueue}
                dataPerQueueVisibility={dataPerQueueVisibility}
                end={endTime}
                start={startTime}
              />
            </>
          )}
        </PerfectScrollbar>
      </div>
    </div>
  );
};

export const SchedulingChartContainer = memo(SchedulingChartContainerComponent);
